import { useState, useEffect, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

function CountdownNumber({ value, label }: { value: number; label: string }) {
  const formatted = String(value).padStart(2, '0');
  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <span className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extralight text-copper tabular-nums">
          {formatted}
        </span>
        {/* Subtle glow behind number */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-copper/5 blur-2xl rounded-full" />
      </div>
      <span className="font-sans text-[10px] md:text-xs uppercase tracking-[3px] text-pearl/50 mt-3 md:mt-4">
        {label}
      </span>
    </div>
  );
}

export default function CountdownSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  // Locked to Indian Standard Time (IST, UTC+5:30) so all guests see the same countdown
  const weddingDate = new Date('2026-06-06T11:30:00+05:30').getTime();

  const calculateTimeLeft = useCallback(() => {
    const now = new Date().getTime();
    const diff = weddingDate - now;

    if (diff <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((diff % (1000 * 60)) / 1000),
    };
  }, [weddingDate]);

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, [calculateTimeLeft]);

  return (
    <section id="countdown" className="relative py-24 md:py-36 bg-midnight-dark overflow-hidden">
      {/* Subtle radial glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-copper/[0.03] blur-[100px] pointer-events-none" />

      <div className="max-w-5xl mx-auto px-6" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, ease: 'easeOut' }}
          className="bg-ink-light/50 backdrop-blur-sm rounded-3xl border border-copper/10 overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.3)]"
        >
          {/* Decorative top border */}
          <div className="h-[2px] bg-gradient-to-r from-transparent via-copper/40 to-transparent" />

          <div className="text-center pt-12 md:pt-16 pb-4 md:pb-6">
            <motion.p
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.3 }}
              className="font-sans text-xs uppercase tracking-[3px] text-copper/60 mb-2"
            >
              Save the Date
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="font-display text-2xl md:text-3xl lg:text-4xl text-cream font-semibold"
            >
              June 6, 2026
            </motion.h2>
          </div>

          {/* Countdown grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-0 px-6 md:px-16 pb-12 md:pb-16">
            <div className="flex flex-col items-center">
              <CountdownNumber value={timeLeft.days} label="Days" />
            </div>
            <div className="relative flex flex-col items-center">
              <div className="hidden md:block absolute left-0 top-1/2 -translate-y-1/2 h-16 w-px bg-copper/10" />
              <CountdownNumber value={timeLeft.hours} label="Hours" />
            </div>
            <div className="relative flex flex-col items-center">
              <div className="hidden md:block absolute left-0 top-1/2 -translate-y-1/2 h-16 w-px bg-copper/10" />
              <CountdownNumber value={timeLeft.minutes} label="Minutes" />
            </div>
            <div className="relative flex flex-col items-center">
              <div className="hidden md:block absolute left-0 top-1/2 -translate-y-1/2 h-16 w-px bg-copper/10" />
              <CountdownNumber value={timeLeft.seconds} label="Seconds" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}